import { ReactElement, useEffect } from 'react';

import { AscProjectTpTicketsView, RdProjectTpTicketsView } from '@neo.tax/shared/graphql';

import { ListFooter, TicketsList } from '../tickets-list';

// Keep in sync with the tickets-list copy of this type: the matching fields are ASC-only (CPC
// per-ticket matching reasoning) and linked pull requests RD-only, so neither can join the Pick
// over the Asc | Rd union.
export type TicketRowTicketType = Pick<
  AscProjectTpTicketsView | RdProjectTpTicketsView,
  'id' | 'identifier' | 'ticketUrl' | 'name' | 'contributorName'
> &
  Partial<Pick<AscProjectTpTicketsView, 'matchingConfidence' | 'matchingReasoning' | 'matchingMethod'>> &
  Partial<Pick<RdProjectTpTicketsView, 'linkedPullRequests'>>;

type GroupingSelection = {
  isGroupingSelected: boolean;
  selectedTicketIds: Set<string>;
};

export interface GroupedTicketsBaseProps {
  groupingId: string;
  tickets: TicketRowTicketType[] | null;
  hasNextPage: boolean;
  loadMore: () => void;
  loading: boolean;
  groupingSelections: Record<string, GroupingSelection>;
  setGroupingSelections: React.Dispatch<React.SetStateAction<Record<string, GroupingSelection>>>;
  isMoveTicketsDisabled: boolean;
  onTicketCountChange?: (groupingId: string, count: number) => void;
}

export const GroupedTicketsBase = ({
  groupingId,
  groupingSelections,
  hasNextPage,
  isMoveTicketsDisabled,
  loadMore,
  loading,
  onTicketCountChange,
  setGroupingSelections,
  tickets,
}: GroupedTicketsBaseProps): ReactElement => {
  // Notify parent component of ticket count for filtering during search
  useEffect(() => {
    if (onTicketCountChange && tickets && !loading) {
      onTicketCountChange(groupingId, tickets.length);
    }
  }, [tickets, loading, onTicketCountChange, groupingId]);

  const currentSelection = groupingSelections[groupingId];
  const isGroupingSelected = currentSelection?.isGroupingSelected ?? false;
  const selectedTicketIds = currentSelection?.selectedTicketIds ?? new Set<string>();

  const handleTicketSelection: React.Dispatch<React.SetStateAction<Set<string>>> = (ticketIdsOrUpdater) => {
    setGroupingSelections((prev) => {
      const currentSelection = prev[groupingId];
      const currentTicketIds = currentSelection?.selectedTicketIds ?? new Set<string>();

      const newTicketIds =
        typeof ticketIdsOrUpdater === 'function' ? ticketIdsOrUpdater(currentTicketIds) : ticketIdsOrUpdater;

      return {
        ...prev,
        [groupingId]: {
          isGroupingSelected: false,
          selectedTicketIds: newTicketIds,
        },
      };
    });
  };

  return (
    <TicketsList
      tickets={tickets ?? []}
      selectedIds={isGroupingSelected ? new Set(tickets?.map((t) => t.id) ?? []) : selectedTicketIds}
      setSelectedIds={isGroupingSelected ? (): void => {} : handleTicketSelection}
      loadMore={() => {}}
      isLoading={loading}
      Footer={(): ReactElement => <ListFooter hasNextPage={hasNextPage} loadMore={loadMore} />}
      loadMoreRowFromBottomThreshold={15}
      isGroupingSelected={isGroupingSelected}
      disableSelection={isMoveTicketsDisabled}
    />
  );
};
